// apps/web/src/pages/vendas/pedido-detalhe-page.tsx — Fase 12, Planos 02 e 21 (PV-01, PV-05, PV-06, D-25, D-37, D-38).
// Detalhe do PV em abas: cabeçalho + Itens (saldos, pendências e lotes exatamente como a API devolve).
// Nenhum saldo é digitado ou recalculado na tela (D-25).
// Plano 23: 'Editar cabeçalho' (pv:editar) com lock otimista de versão e toast de falha genérica.
// Plano 11: menu 'Mais ações' (pv:ajustar) com 'Vincular lote ou remessa existente' (GOV-02, D-39).
// Plano 31: menu '⋯' por item (editar/cancelar item não recebido; ajustar/encerrar saldo de item recebido), 'Adicionar item',
// 'Editar cabeçalho'/'Encerrar saldo'/'Cancelar PV' no menu do PV e aba 'Histórico' (PV-07).
import { useCallback, useEffect, useRef, useState, type ComponentType } from 'react';
import {
  AlertTriangle,
  ArrowUp,
  CalendarX,
  CheckCircle2,
  CircleDashed,
  CircleDollarSign,
  Clock,
  Factory,
  PackageCheck,
  Pencil,
  Plus,
  RefreshCw,
  XCircle,
} from 'lucide-react';
import type { CriarPedidoVendaItemInput, PedidoVendaView, PrioridadePv } from '@pieltec/contracts';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Skeleton } from '../../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../../components/ui/breadcrumb';
import { faixaUrgencia, iconeUrgencia } from '../../lib/kanban/apresentacao';
import { rotuloSituacaoPv, totaisPorUnidade, type IconeSituacaoPv } from '../../lib/vendas/apresentacao';
import { nivelDaPrioridadePv, situacaoPrazoPv } from '../../lib/vendas/filtros';
import { corpoEncerrarSaldo, existeItemComSaldo, itemUnicoComSaldo, ORIENTACAO_ENCERRAR_SALDO_POR_ITEM } from '../../lib/vendas/ajustes';
import { formatarData } from '../../lib/vendas/formatacao';
import { rotuloEstadoExpedicaoRelatorio, rotuloNfOmieRelatorio } from '../../lib/vendas/relatorio';
import { obterSessaoAtual } from '../../lib/session';
import { FechamentoAba } from './fechamento/fechamento-aba';
import { AjustarItemSheet } from './detalhe/ajustar-item-sheet';
import { ConfirmacaoDestrutivaDialog } from './detalhe/confirmacao-destrutiva-dialog';
import { EditarCabecalhoSheet } from './detalhe/editar-cabecalho-sheet';
import { EditarItemSheet } from './detalhe/editar-item-sheet';
import { HistoricoAba } from './detalhe/historico-aba';
import { COPIA_CANCELAR_ITEM, type AcaoItemPv } from './detalhe/item-acoes-menu';
import { ItensTabela, type AcoesDoItem } from './detalhe/itens-tabela';
import { NfsVinculadasAba } from './detalhe/nfs-vinculadas-aba';
import { corpoCancelarPv, MaisAcoesMenu, montarAcoesMaisAcoes } from './detalhe/mais-acoes-menu';
import { VincularExistenteDialog } from './detalhe/vincular-existente-dialog';
import { atalhoDoItemPv } from '../../lib/roteiros/cadastro-peca';
import { RelatorioClienteDialog } from './relatorio/relatorio-cliente-dialog';
import { RelatoriosRecentesPainel } from './relatorio/relatorios-recentes-painel';
import { PedidoItemSheet, type ItemPvForm } from './pedido-item-sheet';
import { usePvComandos } from './use-pv-comandos';

const ROTULO_PRIORIDADE: Record<PrioridadePv, string> = { 0: 'Normal', 10: 'Alta', 20: 'Urgente' };

type IconeLucide = ComponentType<{ className?: string; 'aria-hidden'?: boolean | 'true' | 'false' }>;

const ICONES_SITUACAO: Record<IconeSituacaoPv, IconeLucide> = {
  CircleDashed,
  PackageCheck,
  Factory,
  CircleDollarSign,
  CheckCircle2,
  XCircle,
};

const ICONES_URGENCIA = { CalendarX, AlertTriangle, ArrowUp, Clock } as const;
const ROTULO_URGENCIA: Record<keyof typeof ICONES_URGENCIA, string> = {
  CalendarX: 'Prazo estourado',
  AlertTriangle: 'Prioridade urgente',
  ArrowUp: 'Prioridade alta',
  Clock: 'Prazo próximo',
};

/**
 * Abas do detalhe (D-38). Pontos de extensão: os planos de Fechamento, NFs vinculadas e Histórico
 * acrescentam suas entradas aqui (id estável = valor de `&aba=` na URL); o corpo recebe o PV carregado.
 */
/**
 * D-25: item sem Material mostra "Produto sem cadastro" com "Cadastrar peça"; item com Material mas sem roteiro mostra "Sem roteiro" com
 * "Cadastrar roteiro". "Vincular material" (menu do item) segue para ligar a um material que já existe.
 */
function AtalhosCadastroDoItem({ item, pedidoVendaId, permissoes }: { item: PedidoVendaView['itens'][number]; pedidoVendaId: string; permissoes: readonly string[] }) {
  const pedido = atalhoDoItemPv(item, pedidoVendaId, permissoes);
  if (!pedido) return null;
  return (
    <section aria-label={`Cadastro do item ${item.numeroItem}`} className="flex flex-wrap items-center gap-2">
      <span className="inline-flex items-center rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-xs font-medium text-rose-700">
        {pedido.situacao === 'SEM_CADASTRO' ? 'Produto sem cadastro' : 'Sem roteiro'}
      </span>
      <span className="text-sm text-muted-foreground">
        {pedido.situacao === 'SEM_CADASTRO' ? 'A OP deste item só é gerada depois de cadastrar a peça.' : 'A OP deste item só é gerada depois de cadastrar o roteiro da peça.'}
      </span>
      {pedido.atalho ? (
        <a className="text-sm font-semibold text-blue-700 underline underline-offset-4" href={pedido.atalho.href}>
          {pedido.situacao === 'SEM_CADASTRO' ? 'Cadastrar peça' : 'Cadastrar roteiro'}
        </a>
      ) : null}
    </section>
  );
}

interface ContextoAbas {
  podeAjustar: boolean;
  podeConsultarProducao: boolean;
  /** pv:fechar: habilita a aba Fechamento (12-14). */
  podeFechar: boolean;
  /** Recarrega o PV sem esconder a tela; true quando conseguiu. */
  recarregarPv: () => Promise<boolean>;
  /** Menu "⋯" por item (12-31). */
  acoesItem: AcoesDoItem;
  /** "Adicionar item" na aba Itens (pv:editar, PV não cancelado); ausente = botão oculto. */
  onAdicionarItem?: () => void;
  abrirVincularExistente: () => void;
  /** Recarrega o PV (usado depois de gerar uma OP no lote). */
  recarregar: () => void;
}

interface AbaDetalhe {
  id: string;
  /** Aba exibida só quando a função devolve true (ex.: permissão); ausente = sempre. */
  visivel?: (contexto: ContextoAbas) => boolean;
  rotulo: (pv: PedidoVendaView) => string;
  conteudo: (pv: PedidoVendaView, contexto: ContextoAbas) => React.ReactNode;
}

const ABAS_DETALHE: readonly AbaDetalhe[] = [
  {
    id: 'itens',
    rotulo: () => 'Itens',
    conteudo: (pv, contexto) => (
      <div className="flex flex-col gap-3">
        {contexto.onAdicionarItem && (
          <div className="flex justify-end">
            <Button type="button" variant="outline" size="sm" onClick={contexto.onAdicionarItem}>
              <Plus aria-hidden="true" />
              Adicionar item
            </Button>
          </div>
        )}
        <Card className="overflow-hidden py-0 gap-0">
          <ItensTabela
            itens={pv.itens}
            podeGerarOp={contexto.podeAjustar}
            podeConsultarProducao={contexto.podeConsultarProducao}
            onOpGerada={contexto.recarregar}
            acoesItem={contexto.acoesItem}
            atalhosItem={(item) => <AtalhosCadastroDoItem item={item} pedidoVendaId={pv.id} permissoes={contexto.acoesItem.permissoes} />}
          />
        </Card>
      </div>
    ),
  },
  {
    id: 'fechamento',
    visivel: (contexto) => contexto.podeFechar,
    // 'Fechamento ({N})': N = lotes disponíveis para faturar (omitido se 0). O corpo carrega a lista ao abrir (12-14).
    rotulo: (pv) => (pv.lotesDisponiveisParaFechar > 0 ? `Fechamento (${pv.lotesDisponiveisParaFechar})` : 'Fechamento'),
    conteudo: (pv, contexto) => (
      <FechamentoAba
        pedidoId={pv.id}
        versaoPv={pv.versao}
        numeroNfOmieComprovado={pv.expedicao?.numeroNfOmieComprovado ?? null}
        onRecarregarPv={contexto.recarregarPv}
      />
    ),
  },
  {
    id: 'nfs-vinculadas',
    rotulo: () => 'NFs vinculadas',
    // O conteúdo da aba só é montado ao abrir; a lista de notas é buscada nesse momento (12-11).
    conteudo: (pv, contexto) => (
      <NfsVinculadasAba
        pedidoId={pv.id}
        versaoPv={pv.versao}
        podeVincularExistente={contexto.podeAjustar}
        onVincularExistente={contexto.abrirVincularExistente}
      />
    ),
  },
  {
    id: 'historico',
    rotulo: () => 'Histórico',
    // Carregado só ao abrir a aba (12-31): eventos pv.* do próprio PV, mais recentes primeiro.
    conteudo: (pv) => <HistoricoAba pedidoId={pv.id} versaoPv={pv.versao} />,
  },
];

const ABA_PADRAO = 'itens';

function lerAbaDaUrl(): string {
  const aba = new URLSearchParams(window.location.search).get('aba');
  return aba && ABAS_DETALHE.some((item) => item.id === aba) ? aba : ABA_PADRAO;
}

function Campo({ rotulo, className, children }: { rotulo: string; className?: string; children: React.ReactNode }) {
  return (
    <div className={className}>
      <dt className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{rotulo}</dt>
      <dd>{children}</dd>
    </div>
  );
}

export function PedidoDetalhePage() {
  const id = new URLSearchParams(window.location.search).get('id');
  const [pv, setPv] = useState<PedidoVendaView | null>(null);
  // WR-D1-11: sequenciamento das leituras do PV. Cada `carregar` e cada resultado de mutação avança `seq`; uma resposta de GET
  // que chega depois de um estado mais novo (versao maior) é descartada e não sobrescreve nem a `versao` nem os itens.
  const seq = useRef(0);
  const ultimoPv = useRef<PedidoVendaView | null>(null);
  const montado = useRef(true);
  useEffect(() => {
    montado.current = true;
    return () => {
      montado.current = false;
    };
  }, []);
  /** Aplica o PV devolvido por uma mutação e invalida qualquer GET em voo (que traria um snapshot anterior). */
  const aplicarPv = useCallback((novo: PedidoVendaView): void => {
    seq.current += 1;
    ultimoPv.current = novo;
    setPv(novo);
  }, []);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<'carga' | 'nao-encontrado' | null>(null);
  const [aba, setAba] = useState<string>(lerAbaDaUrl);
  const [permissoes, setPermissoes] = useState<readonly string[]>([]);
  const [acaoItem, setAcaoItem] = useState<{ itemId: string; acao: AcaoItemPv } | null>(null);
  const [cancelarPvAberto, setCancelarPvAberto] = useState(false);
  const [adicionarItemAberto, setAdicionarItemAberto] = useState(false);
  const { cancelarItem, encerrarSaldo, cancelarPv, anexarItens } = usePvComandos();
  const podeEditar = permissoes.includes('pv:editar');
  const podeAjustar = permissoes.includes('pv:ajustar');
  const podeFechar = permissoes.includes('pv:fechar');
  const [edicaoAberta, setEdicaoAberta] = useState(false);
  const [vincularAberto, setVincularAberto] = useState(false);
  const [relatorioAberto, setRelatorioAberto] = useState(false);
  const [relatorioGerado, setRelatorioGerado] = useState(0);
  const [toastFalha, setToastFalha] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    void obterSessaoAtual().then((sessao) => {
      if (!vivo) return;
      setPermissoes(sessao.autenticado ? sessao.usuario.permissions : []);
    });
    return () => {
      vivo = false;
    };
  }, []);

  // Toast de falha genérica (padrão de fila-page.tsx): some sozinho em 5 s.
  useEffect(() => {
    if (!toastFalha) return;
    const timer = window.setTimeout(() => setToastFalha(null), 5000);
    return () => window.clearTimeout(timer);
  }, [toastFalha]);

  // Voltar/avançar do navegador restaura a aba (deep-link `&aba=`).
  useEffect(() => {
    const aoNavegar = () => setAba(lerAbaDaUrl());
    window.addEventListener('popstate', aoNavegar);
    return () => window.removeEventListener('popstate', aoNavegar);
  }, []);

  const trocarAba = (proxima: string) => {
    setAba(proxima);
    const url = new URL(window.location.href);
    url.searchParams.set('aba', proxima);
    window.history.pushState(null, '', `${url.pathname}${url.search}`);
  };

  /** `silencioso`: recarrega sem trocar a tela por skeleton (o Sheet de edição continua montado). */
  const carregar = useCallback(async (sinal?: { vivo: boolean }, silencioso = false): Promise<PedidoVendaView | null> => {
    if (!id) {
      setErro('nao-encontrado');
      setCarregando(false);
      return null;
    }
    const meu = ++seq.current;
    if (!silencioso) setCarregando(true);
    setErro(null);
    try {
      const res = await fetch(`/api/v1/vendas/pedidos/${encodeURIComponent(id)}`, { credentials: 'include' });
      if (sinal && !sinal.vivo) return null;
      if (!montado.current) return null;
      if (meu !== seq.current) return ultimoPv.current;
      if (res.status === 404 || res.status === 400) {
        setErro('nao-encontrado');
        return null;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const atual = (await res.json()) as PedidoVendaView;
      // Superado por uma leitura/mutação mais nova enquanto o corpo chegava: devolve o estado mais novo, sem sobrescrever.
      if (!montado.current || (sinal && !sinal.vivo)) return null;
      if (meu !== seq.current) return ultimoPv.current;
      ultimoPv.current = atual;
      setPv(atual);
      return atual;
    } catch {
      if (meu !== seq.current) return ultimoPv.current;
      if (montado.current && (!sinal || sinal.vivo)) {
        if (silencioso) setToastFalha('Não foi possível recarregar o pedido do cliente. Tente novamente.');
        else setErro('carga');
      }
      return null;
    } finally {
      if (!silencioso && (!sinal || sinal.vivo)) setCarregando(false);
    }
  }, [id]);

  useEffect(() => {
    const sinal = { vivo: true };
    void carregar(sinal);
    return () => {
      sinal.vivo = false;
    };
  }, [carregar]);

  if (carregando) {
    return (
      <div className="mx-auto w-full max-w-7xl p-6 flex flex-col gap-4" aria-busy="true" aria-label="Carregando pedido do cliente">
        <Skeleton className="h-5 w-64" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  if (erro || !pv) {
    return (
      <div className="mx-auto w-full max-w-7xl p-6">
        <Card>
          <CardContent role="alert" className="flex flex-col items-center gap-4 py-12 text-center">
            <p className="text-sm text-rose-700">
              {erro === 'nao-encontrado'
                ? 'Pedido do cliente não encontrado.'
                : 'Não foi possível carregar o pedido do cliente. Verifique a conexão e tente novamente.'}
            </p>
            {erro === 'carga' ? (
              <Button type="button" variant="outline" onClick={() => void carregar()}>
                <RefreshCw aria-hidden="true" />
                Tentar novamente
              </Button>
            ) : (
              <Button asChild variant="outline">
                <a href="/vendas/pedidos">Voltar</a>
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  const situacao = rotuloSituacaoPv(pv.situacao);
  const IconeSituacao = ICONES_SITUACAO[situacao.icone];
  const nivel = nivelDaPrioridadePv(pv.prioridade);
  const prazo = situacaoPrazoPv(pv.prazoSolicitado, pv.situacao);
  const nomeIconeUrgencia = iconeUrgencia(nivel, prazo);
  const IconeUrgencia = nomeIconeUrgencia ? ICONES_URGENCIA[nomeIconeUrgencia] : null;
  const itensAtivos = pv.itens.filter((item) => !item.cancelado).length;
  const pvCancelado = pv.situacao === 'CANCELADO';
  const itemEmAcao = acaoItem ? (pv.itens.find((candidato) => candidato.id === acaoItem.itemId) ?? null) : null;
  const acoesItem: AcoesDoItem = { permissoes, onAcao: (item, acao) => setAcaoItem({ itemId: item.id, acao }) };
  const fecharAcaoItem = (aberto: boolean) => {
    if (!aberto) setAcaoItem(null);
  };
  const recarregarSilencioso = () => carregar(undefined, true);
  const contextoAbas: ContextoAbas = {
    podeAjustar,
    podeConsultarProducao: permissoes.includes('producao:consultar'),
    podeFechar,
    acoesItem,
    onAdicionarItem: podeEditar && !pvCancelado ? () => setAdicionarItemAberto(true) : undefined,
    abrirVincularExistente: () => setVincularAberto(true),
    recarregar: () => void carregar(undefined, true),
    recarregarPv: async () => (await carregar(undefined, true)) !== null,
  };
  const abasVisiveis = ABAS_DETALHE.filter((item) => item.visivel?.(contextoAbas) ?? true);
  const abaAtiva = abasVisiveis.some((item) => item.id === aba) ? aba : ABA_PADRAO;

  // "Encerrar saldo" do PV: age direto com um único item com saldo; com vários, orienta encerrar pelo item.
  const encerrarSaldoDoPv = () => {
    const unico = itemUnicoComSaldo(pv.itens);
    if (unico) setAcaoItem({ itemId: unico.id, acao: 'ENCERRAR_SALDO' });
    else setToastFalha(ORIENTACAO_ENCERRAR_SALDO_POR_ITEM);
  };

  // "Adicionar item": anexa ao PV pelo mesmo POST do formulário; o Sheet só fecha quando a API confirma.
  // Devolve true só quando a API gravou: o Sheet limpa/fecha o formulário apenas nesse caso (WR-D1-03).
  const adicionarItem = async ({ material: _material, precoTabela: _precoTabela, ...novo }: ItemPvForm, continuar: boolean): Promise<boolean> => {
    const item: CriarPedidoVendaItemInput = novo;
    const resultado = await anexarItens(pv.id, { versao: pv.versao, itens: [item] });
    if (resultado.tipo === 'ok') {
      aplicarPv(resultado.resultado);
      if (!continuar) setAdicionarItemAberto(false);
      return true;
    }
    setToastFalha(resultado.message);
    if (resultado.tipo === 'conflito') void carregar(undefined, true);
    return false;
  };

  // Ações do menu "Mais ações": filtradas pela permissão do usuário (a autorização real está no servidor).
  const acoesMenu = montarAcoesMaisAcoes({
    podeEditar,
    podeAjustar,
    pvCancelado,
    temItemComSaldo: existeItemComSaldo(pv.itens),
    onEditarCabecalho: () => setEdicaoAberta(true),
    onVincularExistente: () => setVincularAberto(true),
    onEncerrarSaldo: encerrarSaldoDoPv,
    onCancelarPv: () => setCancelarPvAberto(true),
    onGerarRelatorio: () => setRelatorioAberto(true),
  });

  return (
    <div className="mx-auto w-full max-w-7xl p-6 flex flex-col gap-6">
      <Breadcrumb aria-label="Trilha">
        <BreadcrumbList>
          <BreadcrumbItem>Vendas</BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink href="/vendas/pedidos">Pedidos</BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>{pv.numero}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <Card className="relative overflow-hidden">
        <span aria-hidden="true" className={`absolute inset-y-0 left-0 w-1.5 ${faixaUrgencia({ nivel, prazo })}`} />
        <CardContent className="flex flex-col gap-4 pl-7">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-xl font-semibold leading-tight tabular-nums">Pedido do cliente {pv.numero}</h1>
            <span
              className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${situacao.classes}`}
            >
              <IconeSituacao className="size-3 shrink-0" aria-hidden="true" />
              {situacao.texto}
            </span>
            {(podeEditar || acoesMenu.length > 0) && (
              <div className="ml-auto flex items-center gap-2">
                {podeEditar && !pvCancelado && (
                  <Button type="button" variant="outline" size="sm" onClick={() => setEdicaoAberta(true)}>
                    <Pencil aria-hidden="true" />
                    Editar cabeçalho
                  </Button>
                )}
                <MaisAcoesMenu numeroPv={pv.numero} acoes={acoesMenu} />
              </div>
            )}
          </div>
          <dl className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2 lg:grid-cols-3">
            <Campo rotulo="Cliente">{pv.clienteNome}</Campo>
            <Campo rotulo="Pedido do cliente">{pv.numeroPedidoCliente ?? '—'}</Campo>
            <Campo rotulo="Data">
              <span className="tabular-nums">{formatarData(pv.data)}</span>
            </Campo>
            <Campo rotulo="Prazo solicitado">
              <span className="inline-flex items-center gap-1.5 tabular-nums">
                {IconeUrgencia && nomeIconeUrgencia && (
                  <span title={ROTULO_URGENCIA[nomeIconeUrgencia]} className="inline-flex items-center">
                    <IconeUrgencia className="size-4 shrink-0 text-slate-600" aria-hidden="true" />
                    <span className="sr-only">{ROTULO_URGENCIA[nomeIconeUrgencia]}</span>
                  </span>
                )}
                {formatarData(pv.prazoSolicitado)}
              </span>
            </Campo>
            <Campo rotulo="Prioridade">{ROTULO_PRIORIDADE[pv.prioridade]}</Campo>
            <Campo rotulo="Responsável">{pv.responsavelNome ?? '—'}</Campo>
            {pv.condicaoComercial && <Campo rotulo="Condição comercial">{pv.condicaoComercial}</Campo>}
            {pv.observacoes && (
              <Campo rotulo="Observações" className="sm:col-span-2 lg:col-span-3">
                <span className="whitespace-pre-wrap">{pv.observacoes}</span>
              </Campo>
            )}
          </dl>
          <dl className="flex flex-wrap gap-x-8 gap-y-1 border-t pt-3 text-sm text-muted-foreground">
            <div className="flex gap-1.5">
              <dt>Itens:</dt>
              <dd className="tabular-nums text-foreground">{itensAtivos}</dd>
            </div>
            <div className="flex gap-1.5">
              <dt>Solicitado:</dt>
              <dd className="tabular-nums text-foreground">{totaisPorUnidade(pv.itens, 'solicitado')}</dd>
            </div>
            <div className="flex gap-1.5">
              <dt title="Quantidade incluída em fechamentos do pedido (reservada para expedição)">Fechado:</dt>
              <dd className="tabular-nums text-foreground">{totaisPorUnidade(pv.itens, 'faturado')}</dd>
            </div>
            {pv.expedicao && (
              <>
                <div className="flex gap-1.5">
                  <dt>Expedição:</dt>
                  <dd className="text-foreground">{rotuloEstadoExpedicaoRelatorio(pv.expedicao)}</dd>
                </div>
                <div className="flex gap-1.5">
                  <dt>Prevista / expedida / restante:</dt>
                  <dd className="tabular-nums text-foreground">{pv.expedicao.prevista} / {pv.expedicao.expedida} / {pv.expedicao.restante}</dd>
                </div>
                <div className="flex gap-1.5">
                  <dt>NF Omie:</dt>
                  <dd className="tabular-nums text-foreground">{rotuloNfOmieRelatorio(pv.expedicao)}</dd>
                </div>
              </>
            )}
          </dl>
        </CardContent>
      </Card>

      <Tabs value={abaAtiva} onValueChange={trocarAba} className="gap-4">
        <TabsList aria-label="Seções do pedido do cliente">
          {abasVisiveis.map((item) => (
            <TabsTrigger key={item.id} value={item.id} className="min-h-9 px-4">
              {item.rotulo(pv)}
            </TabsTrigger>
          ))}
        </TabsList>
        {abasVisiveis.map((item) => (
          <TabsContent key={item.id} value={item.id}>
            {item.conteudo(pv, contextoAbas)}
          </TabsContent>
        ))}
      </Tabs>

      {podeEditar && (
        <EditarCabecalhoSheet
          aberto={edicaoAberta}
          onAbertoChange={setEdicaoAberta}
          pv={pv}
          onSalvo={aplicarPv}
          onRecarregar={() => carregar(undefined, true)}
          onFalha={setToastFalha}
        />
      )}

      {podeEditar && (
        <>
          <PedidoItemSheet
            aberto={adicionarItemAberto}
            onAbertoChange={setAdicionarItemAberto}
            cliente={{ id: pv.clienteId, codigo: pv.clienteCodigo, nome: pv.clienteNome }}
            inicial={null}
            onSalvar={adicionarItem}
          />
          <ConfirmacaoDestrutivaDialog
            aberto={cancelarPvAberto}
            onAbertoChange={setCancelarPvAberto}
            titulo="Cancelar PV"
            corpo={corpoCancelarPv(pv.numero)}
            rotuloConfirmar="Cancelar PV"
            onConfirmar={(motivo) => cancelarPv(pv.id, { versao: pv.versao, motivo })}
            onSucesso={aplicarPv}
            onRecarregar={recarregarSilencioso}
            onFalha={setToastFalha}
          />
          <EditarItemSheet
            aberto={acaoItem?.acao === 'EDITAR' && itemEmAcao !== null}
            onAbertoChange={fecharAcaoItem}
            pv={pv}
            item={itemEmAcao}
            onSalvo={aplicarPv}
            onRecarregar={recarregarSilencioso}
            onFalha={setToastFalha}
          />
          {itemEmAcao && (
            <ConfirmacaoDestrutivaDialog
              aberto={acaoItem?.acao === 'CANCELAR'}
              onAbertoChange={fecharAcaoItem}
              titulo={`Cancelar item ${itemEmAcao.numeroItem}`}
              corpo={COPIA_CANCELAR_ITEM}
              rotuloConfirmar="Cancelar item"
              onConfirmar={(motivo) => cancelarItem(pv.id, itemEmAcao.id, { versao: itemEmAcao.versao, motivo })}
              onSucesso={aplicarPv}
              onRecarregar={recarregarSilencioso}
              onFalha={setToastFalha}
            />
          )}
        </>
      )}

      {podeAjustar && (
        <>
          <AjustarItemSheet
            aberto={acaoItem?.acao === 'AJUSTAR' && itemEmAcao !== null}
            onAbertoChange={fecharAcaoItem}
            pv={pv}
            item={itemEmAcao}
            onAtualizado={aplicarPv}
            onRecarregar={recarregarSilencioso}
            onFalha={setToastFalha}
          />
          {itemEmAcao && (
            <ConfirmacaoDestrutivaDialog
              aberto={acaoItem?.acao === 'ENCERRAR_SALDO'}
              onAbertoChange={fecharAcaoItem}
              titulo="Encerrar saldo"
              corpo={corpoEncerrarSaldo({ saldo: itemEmAcao.saldos.saldo, numeroItem: itemEmAcao.numeroItem, unidade: itemEmAcao.unidade })}
              rotuloConfirmar="Encerrar saldo"
              onConfirmar={(motivo) => encerrarSaldo(pv.id, itemEmAcao.id, { versao: itemEmAcao.versao, motivo })}
              onSucesso={aplicarPv}
              onRecarregar={recarregarSilencioso}
              onFalha={setToastFalha}
            />
          )}
        </>
      )}

      {podeAjustar && (
        <VincularExistenteDialog
          aberto={vincularAberto}
          onAbertoChange={setVincularAberto}
          pv={pv}
          onVinculado={aplicarPv}
          onRecarregar={() => void carregar(undefined, true)}
        />
      )}

      <RelatorioClienteDialog
        aberto={relatorioAberto}
        onAbertoChange={setRelatorioAberto}
        clienteInicial={{ id: pv.clienteId, codigo: pv.clienteCodigo, nome: pv.clienteNome }}
        clienteTravado
        onGerado={() => setRelatorioGerado((n) => n + 1)}
      />

      {relatorioGerado > 0 && <RelatoriosRecentesPainel clienteId={pv.clienteId} atualizacao={relatorioGerado} />}

      {toastFalha && (
        <div
          role="status"
          className="fixed bottom-6 right-6 bg-slate-900 text-white px-4 py-3 rounded-xl shadow-2xl flex items-center gap-3 z-50 animate-in fade-in slide-in-from-bottom-2 duration-150"
        >
          <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0" aria-hidden="true" />
          <span className="text-xs font-semibold">{toastFalha}</span>
        </div>
      )}
    </div>
  );
}
